/**
 * metaPixel.js — San Tomas Tourism College
 * ---------------------------------------------------------------
 * Consent-gated Meta Pixel loader for a React single-page app.
 *
 * Why this exists instead of pasting the raw snippet into index.html:
 *
 *  1. The raw snippet fires PageView exactly once, on hard page load.
 *     This site is a react-router SPA, so a visitor who lands on "/"
 *     and then browses to /programs -> /programs/31 -> /enrollment
 *     generates ONE PageView. Meta would see a site nobody navigates,
 *     and would optimise ads against a landing page rather than against
 *     applications.
 *
 *  2. No script loads until the visitor has actually accepted tracking
 *     (Kenya Data Protection Act 2019 — consent must be express,
 *     informed and freely given before non-essential processing).
 *
 *  3. The pixel never runs on localhost, preview builds, or /admin,
 *     so staff activity and developer refreshes stay out of the data.
 *
 *  4. Every event carries an eventID so the same events can later be
 *     mirrored server-side via the Conversions API without double
 *     counting. Nothing here needs to change when that day comes.
 *
 * No personally identifying data is ever passed to Meta from this file.
 * See docs/DATA-PROTECTION.md before adding any new parameter.
 */

import { hasConsent, CONSENT_EVENT } from './consent';

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const ALLOW_IN_DEV = process.env.REACT_APP_PIXEL_ALLOW_DEV === 'true';

/* Routes the pixel must never observe. Prefix match, case-insensitive. */
const EXCLUDED_PATHS = ['/admin', '/login', '/dashboard'];

/* Module state */
let scriptRequested = false;   // have we injected connect.facebook.net yet
let initialised = false;       // has fbq('init') run
const pending = [];            // events captured before consent / before load

/* ── Environment checks ─────────────────────────────────────────── */

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

/** True on localhost, LAN IPs and *.local — i.e. developer machines. */
const isLocalHost = () => {
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '[::1]' ||
    h.endsWith('.local') ||
    /^192\.168\./.test(h) ||
    /^10\./.test(h)
  );
};

/** Visitor has asked, at browser level, not to be tracked. */
const privacySignalSet = () =>
  window.navigator?.globalPrivacyControl === true ||
  window.navigator?.doNotTrack === '1' ||
  window.doNotTrack === '1';

export const isExcludedPath = (pathname = window.location.pathname) =>
  EXCLUDED_PATHS.some((p) => pathname.toLowerCase().startsWith(p));

/** Every gate that must be open before a single byte is requested. */
const canLoad = () => {
  if (!isBrowser()) return false;
  if (!PIXEL_ID) return false;
  if (isLocalHost() && !ALLOW_IN_DEV) return false;
  if (privacySignalSet()) return false;
  if (isExcludedPath()) return false;
  return hasConsent();
};

/* ── Event ID (for future Conversions API deduplication) ────────── */

const newEventId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `stc-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/* ── Script injection ───────────────────────────────────────────── */

/**
 * The official Meta base code, rewritten as a module.
 * Injected lazily so it never competes with first paint.
 */
const injectBaseCode = () => {
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
};

const whenIdle = (fn) => {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(fn, { timeout: 2000 });
  } else {
    setTimeout(fn, 1200);
  }
};

/**
 * Load and initialise the pixel. Safe to call repeatedly — it will only
 * ever do the work once, and only when every gate in canLoad() is open.
 */
export const initPixel = () => {
  if (initialised || scriptRequested) return;
  if (!canLoad()) return;

  scriptRequested = true;

  whenIdle(() => {
    injectBaseCode();

    /* Automatic Advanced Matching is disabled deliberately. If left on,
       Meta's script reads values out of any form on the page — including
       the national ID, date of birth and school fields on /enrollment.
       That must not leave this site. Also switch it off in Events Manager;
       this flag alone is not enough. */
    window.fbq('init', PIXEL_ID, {}, { autoConfig: false });

    initialised = true;

    /* The base code's own PageView, plus anything captured while the
       visitor was still reading the consent banner. */
    fire('PageView', {}, { standard: true });
    while (pending.length) {
      const q = pending.shift();
      fire(q.name, q.params, q.opts);
    }
  });
};

/* ── Event dispatch ─────────────────────────────────────────────── */

const fire = (name, params = {}, opts = {}) => {
  if (!window.fbq) return;
  const eventID = opts.eventID || newEventId();
  window.fbq(opts.standard === false ? 'trackCustom' : 'track', name, params, { eventID });

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.info('[pixel]', name, params, eventID);
  }
  return eventID;
};

/**
 * Track a standard Meta event (PageView, Lead, ViewContent, Contact,
 * InitiateCheckout, CompleteRegistration, Search...).
 *
 * Returns the eventID, so a server-side Conversions API call can send
 * the same event_id and let Meta deduplicate.
 */
export const trackEvent = (name, params = {}) => {
  if (isExcludedPath()) return null;

  if (!initialised) {
    /* Not consented yet, or script still loading. Hold it — do not drop
       it. If consent never arrives the queue is simply discarded. */
    pending.push({ name, params, opts: { standard: true } });
    initPixel();
    return null;
  }
  return fire(name, params, { standard: true });
};

/** Track a custom (non-standard) event. Same rules apply. */
export const trackCustomEvent = (name, params = {}) => {
  if (isExcludedPath()) return null;
  if (!initialised) {
    pending.push({ name, params, opts: { standard: false } });
    initPixel();
    return null;
  }
  return fire(name, params, { standard: false });
};

/** Called by the consent banner when the visitor withdraws consent. */
export const disablePixel = () => {
  pending.length = 0;
  if (window.fbq) {
    try {
      window.fbq('consent', 'revoke');
    } catch (e) {
      /* fbq not ready — nothing to revoke */
    }
  }
};

/* React to a consent decision made anywhere in the app. */
if (isBrowser()) {
  window.addEventListener(CONSENT_EVENT, (e) => {
    if (e.detail?.granted) initPixel();
    else disablePixel();
  });
}

export const pixelIsActive = () => initialised;
